import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, can, type ExerciseKind, type NotionDetail, type ParamSpec, type PreviewInstance, type Template } from "../lib/api";
import { useStudio } from "../lib/context";
import { Badge, errorMessage, Field } from "./ui";

const KINDS: { v: ExerciseKind; l: string }[] = [
  { v: "NUMERIC", l: "Réponse numérique" }, { v: "MCQ", l: "QCM" }, { v: "EXPRESSION", l: "Expression" },
  { v: "DRAG_POINT", l: "Placer un point" }, { v: "ORDERING", l: "Remettre dans l'ordre" }, { v: "OPEN", l: "Réponse ouverte" },
];
type ParamRow = { name: string; min: string; max: string; int: boolean; nonZero: boolean };
type Form = { kind: ExerciseKind; difficulty: number; solverKey: string; prompts: Record<string, string>; hints: Record<string, string>; params: ParamRow[] };

const paramRow = (name: string): ParamRow => ({ name, min: "1", max: "9", int: true, nonZero: false });
const emptyForm = (solver?: { key: string; params: string[] }): Form => ({ kind: "NUMERIC", difficulty: 2, solverKey: solver?.key ?? "", prompts: { fr: "" }, hints: { fr: "" }, params: (solver?.params ?? ["a"]).map(paramRow) });

function fromTemplate(t: Template): Form {
  return {
    kind: t.kind, difficulty: t.difficulty, solverKey: t.solverKey,
    prompts: { ...t.promptTemplate },
    hints: Object.fromEntries(Object.entries(t.hints).map(([k, v]) => [k, v.join("\n")])),
    params: Object.entries(t.paramsSchema).map(([name, p]) => ({ name, min: String(p.min), max: String(p.max), int: !!p.int, nonZero: !!p.nonZero })),
  };
}

/** Formulaire → gabarit, avec les erreurs de saisie détectées avant tout envoi. */
function toTemplate(f: Form): { t: Template | null; errors: string[] } {
  const errors: string[] = [];
  if (!f.solverKey) errors.push("Choisissez un solveur.");
  if (!f.prompts.fr?.trim()) errors.push("L'énoncé en français est obligatoire.");
  const paramsSchema: Record<string, ParamSpec> = {};
  for (const p of f.params) {
    if (!/^[a-zA-Z]\w{0,15}$/.test(p.name)) { errors.push(`Nom de paramètre invalide : « ${p.name} »`); continue; }
    if (paramsSchema[p.name]) errors.push(`Paramètre en double : ${p.name}`);
    const min = Number(p.min), max = Number(p.max);
    if (p.min === "" || p.max === "" || !Number.isFinite(min) || !Number.isFinite(max)) { errors.push(`Bornes invalides pour ${p.name}`); continue; }
    if (max < min) errors.push(`${p.name} : max doit être ≥ min`);
    if (p.nonZero && min === 0 && max === 0) errors.push(`${p.name} : ne peut pas être non nul si min = max = 0`);
    paramsSchema[p.name] = { min, max, ...(p.int ? { int: true } : {}), ...(p.nonZero ? { nonZero: true } : {}) };
  }
  if (f.params.length > 12) errors.push("12 paramètres au plus.");
  const promptTemplate = Object.fromEntries(Object.entries(f.prompts).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()]));
  for (const [lang, text] of Object.entries(promptTemplate)) {
    for (const m of text.matchAll(/\{(\w+)\}/g)) if (!paramsSchema[m[1]]) errors.push(`Énoncé (${lang}) : {${m[1]}} n'est pas un paramètre.`);
  }
  const hints = Object.fromEntries(Object.entries(f.hints).map(([k, v]) => [k, v.split("\n").map((s) => s.trim()).filter(Boolean)]).filter(([, v]) => v.length));
  for (const [lang, hs] of Object.entries(hints) as [string, string[]][]) if (hs.length > 3) errors.push(`3 indices au plus (${lang}).`);
  if (errors.length) return { t: null, errors };
  return { t: { kind: f.kind, difficulty: f.difficulty, solverKey: f.solverKey, promptTemplate, paramsSchema, hints }, errors };
}

export function ExerciseEditor({ notion }: { notion: NotionDetail }) {
  const { teacher, languages, toast, countryId } = useStudio();
  const qc = useQueryClient();
  const solvers = useQuery({ queryKey: ["solvers"], queryFn: api.solvers, staleTime: Infinity });
  const [form, setForm] = useState<Form | null>(null);
  const [previewLang, setPreviewLang] = useState("fr");
  const [preview, setPreview] = useState<{ key: string; instances: PreviewInstance[]; problems: number } | null>(null);
  const { t, errors } = useMemo(() => (form ? toTemplate(form) : { t: null, errors: [] }), [form]);
  const formKey = t ? JSON.stringify(t) : "";
  const previewFresh = !!preview && preview.key === formKey;

  const refresh = async () => { await qc.refetchQueries({ queryKey: ["notion", notion.id] }); void qc.invalidateQueries({ queryKey: ["curricula", countryId] }); void qc.invalidateQueries({ queryKey: ["dashboard", countryId] }); };
  const runPreview = useMutation({
    mutationFn: () => api.previewExercise(t!, previewLang, 10),
    onSuccess: (r) => setPreview({ key: formKey, ...r }),
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const save = useMutation({
    mutationFn: async () => { const r = await api.saveExercise(notion.id, t!); await refresh(); return r; },
    onSuccess: () => { toast("Gabarit enregistré, en attente de relecture"); setForm(null); setPreview(null); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const review = useMutation({
    mutationFn: async ({ id, d }: { id: string; d: "VALIDATED" | "REJECTED" }) => { await api.reviewExercise(id, d); await refresh(); return d; },
    onSuccess: (d) => toast(d === "VALIDATED" ? "Gabarit validé" : "Gabarit refusé"),
    onError: (e) => toast(errorMessage(e), "err"),
  });

  const expected = solvers.data?.find((s) => s.key === form?.solverKey)?.params ?? [];
  const missing = expected.filter((p) => !form?.params.some((r) => r.name === p));
  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const setParam = (i: number, patch: Partial<ParamRow>) => setForm((f) => (f ? { ...f, params: f.params.map((p, k) => (k === i ? { ...p, ...patch } : p)) } : f));

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="card">
        <div className="row" style={{ marginBottom: 10 }}>
          <h2>Gabarits</h2>
          <span className="muted small">Chaque gabarit génère une infinité d'exercices : paramètres tirés au hasard, réponse calculée par le solveur.</span>
          <span className="spacer" />
          {can.author(teacher) && !form && <button className="btn sm" onClick={() => { setForm(emptyForm(solvers.data?.[0])); setPreview(null); }}>+ Nouveau gabarit</button>}
        </div>
        {notion.exercises.length === 0 ? <div className="empty">Aucun gabarit pour cette notion.</div> : (
          <table className="t">
            <thead><tr><th>Énoncé</th><th>Type</th><th>Niv.</th><th>Solveur</th><th>Statut</th><th /></tr></thead>
            <tbody>
              {notion.exercises.map((e) => (
                <tr key={e.id}>
                  <td style={{ maxWidth: 380 }}>{e.promptTemplate.fr}</td>
                  <td className="small">{KINDS.find((k) => k.v === e.kind)?.l ?? e.kind}</td>
                  <td>{e.difficulty}</td>
                  <td className="mono small">{e.solverKey}</td>
                  <td><Badge status={e.status} /></td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {e.status === "IN_REVIEW" && can.reviewExercise(teacher) && e.authorId !== teacher?.id && (
                      <>
                        <button className="btn sm" disabled={review.isPending} onClick={() => review.mutate({ id: e.id, d: "VALIDATED" })}>Valider</button>{" "}
                        <button className="btn sm danger" disabled={review.isPending} onClick={() => { if (window.confirm("Refuser ce gabarit ?")) review.mutate({ id: e.id, d: "REJECTED" }); }}>Refuser</button>{" "}
                      </>
                    )}
                    {can.author(teacher) && <button className="btn sm ghost" onClick={() => { setForm(fromTemplate(e)); setPreview(null); }}>Dupliquer</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {form && (
        <div className="card stack appear">
          <div className="row"><h2>Nouveau gabarit</h2><span className="spacer" /><button className="btn sm ghost" onClick={() => { setForm(null); setPreview(null); }}>Fermer</button></div>
          <div className="row" style={{ flexWrap: "wrap", alignItems: "flex-start" }}>
            <Field label="Type"><select className="select" value={form.kind} onChange={(e) => set({ kind: e.target.value as ExerciseKind })}>{KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}</select></Field>
            <Field label="Difficulté (1–5)"><input className="input" type="number" min={1} max={5} style={{ width: 90 }} value={form.difficulty} onChange={(e) => set({ difficulty: Math.max(1, Math.min(5, Math.round(Number(e.target.value)) || 1)) })} /></Field>
            <Field label="Solveur (calcule la bonne réponse)">
              <select className="select mono" value={form.solverKey} onChange={(e) => set({ solverKey: e.target.value })}>
                <option value="">—</option>
                {solvers.data?.map((s) => <option key={s.key} value={s.key}>{s.key}</option>)}
              </select>
            </Field>
            {expected.length > 0 && (
              <div className="field">
                <span>Paramètres attendus</span>
                <div className="row" style={{ height: 36 }}>
                  <span className="mono">{expected.map((p) => `{${p}}`).join(" ")}</span>
                  {missing.length > 0 && <button type="button" className="btn sm amber" onClick={() => setForm((f) => f && { ...f, params: [...f.params, ...missing.map(paramRow)] })}>Ajouter {missing.join(", ")}</button>}
                </div>
              </div>
            )}
          </div>

          <div className="stack">
            <h3>Paramètres tirés au hasard</h3>
            <table className="t">
              <thead><tr><th>Nom</th><th>Min</th><th>Max</th><th>Entier</th><th>Non nul</th><th /></tr></thead>
              <tbody>
                {form.params.map((p, i) => (
                  <tr key={i}>
                    <td><input className="input mono" style={{ width: 90 }} value={p.name} maxLength={16} onChange={(e) => setParam(i, { name: e.target.value.trim() })} /></td>
                    <td><input className="input" type="number" style={{ width: 90 }} value={p.min} onChange={(e) => setParam(i, { min: e.target.value })} /></td>
                    <td><input className="input" type="number" style={{ width: 90 }} value={p.max} onChange={(e) => setParam(i, { max: e.target.value })} /></td>
                    <td><input type="checkbox" checked={p.int} onChange={(e) => setParam(i, { int: e.target.checked })} /></td>
                    <td><input type="checkbox" checked={p.nonZero} onChange={(e) => setParam(i, { nonZero: e.target.checked })} /></td>
                    <td><button className="icon-btn" aria-label="Retirer" onClick={() => setForm((f) => f && { ...f, params: f.params.filter((_, k) => k !== i) })}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div><button className="btn sm ghost" disabled={form.params.length >= 12} onClick={() => setForm((f) => f && { ...f, params: [...f.params, { name: String.fromCharCode(97 + f.params.length), min: "1", max: "9", int: true, nonZero: false }] })}>+ Paramètre</button></div>
          </div>

          {languages.map((l) => (
            <div key={l.code} className="row" style={{ alignItems: "flex-start", flexWrap: "wrap" }}>
              <div style={{ flex: "2 1 320px" }}>
                <Field label={`Énoncé · ${l.nativeName ?? l.name}${l.code === "fr" ? " (obligatoire)" : ""}`} hint={l.code === "fr" ? "Utilisez {a}, {b}… pour insérer les paramètres." : undefined}>
                  <textarea className="textarea" maxLength={600} value={form.prompts[l.code] ?? ""} onChange={(e) => set({ prompts: { ...form.prompts, [l.code]: e.target.value } })} />
                </Field>
              </div>
              <div style={{ flex: "1 1 240px" }}>
                <Field label={`Indices · ${l.nativeName ?? l.name}`} hint="Un par ligne, 3 au plus, du plus léger au plus explicite.">
                  <textarea className="textarea" maxLength={1200} value={form.hints[l.code] ?? ""} onChange={(e) => set({ hints: { ...form.hints, [l.code]: e.target.value } })} />
                </Field>
              </div>
            </div>
          ))}

          {errors.length > 0 && <div className="alert err">{errors.join(" · ")}</div>}

          <div className="row" style={{ flexWrap: "wrap" }}>
            {languages.length > 1 && (
              <select className="select" style={{ width: 150 }} value={previewLang} onChange={(e) => setPreviewLang(e.target.value)} aria-label="Langue de l'aperçu">
                {languages.map((l) => <option key={l.code} value={l.code}>{l.nativeName ?? l.name}</option>)}
              </select>
            )}
            <button className="btn ghost" disabled={!t || runPreview.isPending} onClick={() => runPreview.mutate()}>{runPreview.isPending ? "Tirage…" : "Tirer 10 instances"}</button>
            <span className="spacer" />
            <span className="muted small">{!previewFresh ? "Tirez des instances pour vérifier le gabarit avant de l'enregistrer." : preview!.problems ? "Corrigez les instances en erreur." : "Les 10 instances sont correctes."}</span>
            <button className="btn" disabled={!t || !previewFresh || preview!.problems > 0 || save.isPending} onClick={() => save.mutate()}>Enregistrer et soumettre</button>
          </div>

          {preview && (
            <div className="appear" style={{ opacity: previewFresh ? 1 : 0.5, transition: "opacity 200ms ease" }}>
              {preview.problems > 0 && <div className="alert err" style={{ marginBottom: 8 }}>{preview.problems} instance{preview.problems > 1 ? "s" : ""} sur 10 en erreur : ajustez les bornes (ex. cochez « non nul » pour un diviseur).</div>}
              <table className="t">
                <thead><tr><th>Paramètres</th><th>Énoncé</th><th>Réponse</th><th>Indices</th></tr></thead>
                <tbody>
                  {preview.instances.map((i, k) => (
                    <tr key={k} className={i.problem ? "problem" : ""}>
                      <td className="mono small">{Object.entries(i.params).map(([n, v]) => `${n}=${v}`).join(" ")}</td>
                      <td>{i.prompt}</td>
                      <td className="mono">{i.problem ? <span style={{ color: "var(--bad)", fontWeight: 700 }}>{i.problem}</span> : String(i.answer)}</td>
                      <td className="small muted">{i.hints.join(" → ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
